import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './fraud-indicator-card.module.less';
import { messages } from './messages';
import { useDismissFraudIndicator } from './use-dismiss-fraud-indicator';
import { BLOCKING_BEHAVIOR, BLOCKING_CONFIG, FRAUD_INDICATOR_CODES } from '../../../../constants/fraud-constants';
import { useSellerFormattedDate } from '../../../../hooks/use-datetime-formats';

import type { FraudIndicator } from '../../../../types/fraud-detection';

export const FraudIndicatorCard = ({
  fraudIndicator,
  fulfillmentPlanId,
}: {
  fraudIndicator: FraudIndicator;
  fulfillmentPlanId: string;
}) => {
  const { fraudIndicatorName, fraudIndicatorCode, category, appliedDateUtc, blockingBehavior, metadata } = fraudIndicator;
  const matchScore =
    fraudIndicatorCode === FRAUD_INDICATOR_CODES.BLACKLIST_FUZZY && typeof metadata?.match_score === 'number'
      ? metadata.match_score
      : undefined;
  const config = BLOCKING_CONFIG[blockingBehavior];
  const formattedDate = useSellerFormattedDate(appliedDateUtc);
  const { dismissIndicator } = useDismissFraudIndicator();

  const isBlocking = blockingBehavior === BLOCKING_BEHAVIOR.BLOCKS;
  const dismissButtonText = isBlocking ? messages.unblockButton : messages.removeAlertButton;

  const handleDismiss = () => {
    dismissIndicator(fulfillmentPlanId, fraudIndicatorCode, fraudIndicator);
  };

  const titleValue = matchScore !== undefined ? messages.indicatorWithMatchScore : fraudIndicatorName;

  return (
    <div className={style['fraud-indicator-card']}>
      <FlexRow gap={Gap.Space8}>
        <FlexColumn className={style['content-section']} gap={Gap.Space8}>
          <Text
            className={style['card-title']}
            formattedMessageValues={matchScore !== undefined ? { name: fraudIndicatorName, score: matchScore } : undefined}
            textType={TextType.H4}
            value={titleValue}
          />
          <FlexRow className={`${style['blocking-badge']} ${style[config.className]}`} gap={Gap.Space4}>
            <FontAwesomeIcon className={style['blocking-icon']} icon={config.icon} />
            <Text textType={TextType.Caption} value={messages[config.messageKey]} />
          </FlexRow>
        </FlexColumn>
        <Button className={style['dismiss-button']} onClick={handleDismiss} theme={THEME_CONSTANTS.secondary}>
          <FormattedMessage {...dismissButtonText} />
        </Button>
      </FlexRow>
      <FlexRow className={style['card-description-wrapper']}>
        <Text
          className={style['card-description']}
          formattedMessageValues={{ category }}
          textType={TextType.Description}
          value={messages.categoryLabel}
        />
        <Text className={style['card-description-separator']} textType={TextType.Description} value=' • ' />
        <Text
          className={style['card-description-date']}
          formattedMessageValues={{ date: formattedDate }}
          textType={TextType.Description}
          value={messages.appliedDateLabel}
        />
      </FlexRow>
    </div>
  );
};
